import { DocumentRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import { ChangeEvent, DragEvent, useState } from 'react';

import { Icon } from '../icon';
import { IconButton } from '../shared';
import { FilePickerProps } from './file-picker.model';
import { fieldPikerStyles } from './file-picker.styles';
import useFilePicker from './useFilePicker';

const FilePicker = <T extends boolean = false>({
  value,
  multiple,
  allowedExtensions = [],
  onChange,
  onError,
  handleDownload,
}: FilePickerProps<T>) => {
  const styles = fieldPikerStyles();
  const [isDragging, setIsDragging] = useState(false);

  const { files, handleFiles, removeFile } = useFilePicker<T>({
    value,
    multiple,
    allowedExtensions,
    onChange,
    onError,
  });

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    handleFiles(selectedFiles);
  };

  const handleDrop = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  };

  const handleDragOver = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragEnter = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div>
      {(multiple || (!multiple && !files.length)) && (
        <label
          tabIndex={0}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          htmlFor="file-upload"
          aria-label="Upload a file"
          className={cn(styles.label, { [styles.draggingBorder]: isDragging })}
          data-testid={`file-upload`}
        >
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            multiple={multiple}
            aria-label="Select files"
            accept={allowedExtensions.join(',')}
          />
          <Icon iconName="ArrowUpload24Regular" />
          {isDragging ? (
            <>
              <p className={styles.draggingLabel}>Drop the file here</p>
              <span>&nbsp;</span>
            </>
          ) : (
            <>
              <p>
                <span className={styles.blueText}>Choose file</span> or{' '}
                <span className={styles.strong}>drag here</span> to upload
              </p>
              {allowedExtensions.length > 0 ? (
                <span>
                  Supports:
                  {allowedExtensions.map((ext) => ext.toUpperCase()).join(', ')}
                </span>
              ) : (
                <span>&nbsp;</span>
              )}
            </>
          )}
        </label>
      )}

      {files.length > 0 && (
        <div className={styles.fileList}>
          <div className={styles.fileListHeader}>File</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {files.map((file) => (
              <div key={file.name} className={styles.fileItem}>
                <div className={styles.fileNameContainer}>
                  <DocumentRegular
                    style={{ color: '#0078d4', flexShrink: 0 }}
                  />
                  <span className={styles.fileName} title={file.name}>
                    {file.name}
                  </span>
                </div>

                <div className={styles.fileActions}>
                  {handleDownload && (
                    <IconButton
                      icon={
                        <Icon color="blue" iconName="ArrowDownloadRegular" />
                      }
                      onClick={() => handleDownload(file)}
                      title="Download file"
                    />
                  )}
                  <IconButton
                    icon={<Icon color="red" iconName="Delete16Filled" />}
                    onClick={() => removeFile(file)}
                    title="Remove file"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilePicker;
